/* Slate & Fold inquiry attribution v1. No commerce hooks, extra tag or consent override. */
(() => {
  'use strict';
  const script = document.getElementById('slate-inquiry-attribution');
  if (!script || window.__slateInquiryStarted) return;
  const ownHost = host => /^(?:www\.|au\.)?slateandfold\.com$/.test(host);
  const publicPath = path => /^\/(?:$|products\/|collections(?:\/|$)|pages\/|blogs\/|policies\/)/.test(path.replace(/^\/en-gb(?:\/|$)/, '/'))
    && !/(?:logo|customizer|checkout|account)/i.test(path);
  if (!ownHost(location.hostname) || !publicPath(location.pathname)) return;
  window.__slateInquiryStarted = true;
  const live = script.dataset.themeRole === 'main' && script.dataset.designMode !== 'true'
    && window.Shopify?.designMode !== true && !new URLSearchParams(location.search).has('preview_theme_id');
  const namespace = live ? 'sf_inquiry_v1' : 'sf_inquiry_qa_' + script.dataset.themeId;
  const ttl = 30 * 86400000;
  const clickKeys = ['gclid', 'gbraid', 'wbraid', 'msclkid', 'fbclid'];
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  let state = null, allowed = false, privacyReady = false, cookieAvailable = false;
  let form, leadId = '', successHandled = false, retryCount = 0;
  const clean = (value, max = 100) => {
    const text = String(value || '').trim();
    // Reject URLs, email-like or long phone-number-like values, not just truncate PII.
    return /[@<>\r\n]|https?:|\d[\d\s()+.-]{8,}\d/i.test(text) ? '' : text.slice(0, max);
  };
  const pageUrl = () => location.origin + location.pathname;
  const safeUrl = value => {
    try {
      const url = new URL(value);
      return ownHost(url.hostname) && publicPath(url.pathname) && !url.username && !url.password
        && !/%40|@/i.test(url.pathname) ? url.origin + url.pathname.slice(0, 250) : '';
    } catch (_) { return ''; }
  };
  const category = source => {
    const s = String(source || '').toLowerCase();
    if (/^(?:google|google\.[a-z.]+|www\.google\.[a-z.]+)$/.test(s)) return 'google';
    if (/^(?:bing|(?:www\.)?bing\.com)$/.test(s)) return 'bing';
    if (/^(?:chatgpt|chatgpt\.com|chat\.openai\.com)$/.test(s)) return 'chatgpt';
    if (/^(?:facebook|instagram|meta|(?:l\.|m\.|www\.)?(?:facebook|instagram)\.com)$/.test(s)) return 'meta';
    if (s === 'unattributed') return s;
    return s ? 'other_attributed' : 'unavailable';
  };
  function touch() {
    const params = new URLSearchParams(location.search);
    const t = { at: Date.now(), landing_page: safeUrl(pageUrl()), referrer_host: '', source: 'unattributed' };
    try {
      const host = new URL(document.referrer).hostname.toLowerCase();
      if (!ownHost(host) && !host.endsWith('.myshopify.com') && host !== 'admin.shopify.com') t.referrer_host = clean(host);
    } catch (_) { /* absent referrer is not proof of Direct */ }
    utmKeys.forEach(key => { t[key] = clean(params.get(key)); });
    clickKeys.forEach(key => {
      const value = params.get(key) || '';
      t[key] = /^[A-Za-z0-9_-]{1,180}$/.test(value) ? value : '';
    });
    t.source = t.utm_source || (t.gclid || t.gbraid || t.wbraid ? 'google' : t.msclkid ? 'bing' : t.fbclid ? 'meta' : t.referrer_host || 'unattributed');
    t.attributed = !!(t.referrer_host || utmKeys.some(k => t[k]) || clickKeys.some(k => t[k]));
    return t;
  }
  // Entry data stays only in memory until the Shopify privacy API allows processing.
  const entry = touch();
  function normalize(t) {
    if (!t || !Number.isFinite(t.at) || Date.now() - t.at > ttl || t.at > Date.now() + 60000) return null;
    const url = safeUrl(t.landing_page);
    if (!url) return null;
    const result = { at: t.at, landing_page: url, source: clean(t.source), referrer_host: clean(t.referrer_host), attributed: t.attributed === true };
    utmKeys.forEach(k => { result[k] = clean(t[k]); });
    clickKeys.forEach(k => { result[k] = /^[A-Za-z0-9_-]{1,180}$/.test(t[k] || '') ? t[k] : ''; });
    return result;
  }
  function readCookie(suffix) {
    try {
      const prefix = namespace + suffix + '=';
      const value = document.cookie.split('; ').find(c => c.startsWith(prefix));
      return value ? normalize(JSON.parse(decodeURIComponent(value.slice(prefix.length)))) : null;
    } catch (_) { return null; }
  }
  function saveCookie(suffix, data) {
    try {
      const value = encodeURIComponent(JSON.stringify(data));
      if (value.length > 3500) return false;
      const age = Math.max(0, Math.floor((ttl - (Date.now() - data.at)) / 1000));
      document.cookie = namespace + suffix + '=' + value + '; Max-Age=' + age + '; Path=/; Domain=slateandfold.com; Secure; SameSite=Lax';
      return !!readCookie(suffix);
    } catch (_) { return false; }
  }
  function pending(action, value) {
    try {
      const key = namespace + '_pending';
      if (action === 'set') { sessionStorage.setItem(key, JSON.stringify(value)); return true; }
      if (action === 'remove') { sessionStorage.removeItem(key); return true; }
      return JSON.parse(sessionStorage.getItem(key) || 'null');
    } catch (_) { return null; }
  }
  function clearTracking() {
    state = null; cookieAvailable = false;
    for (const suffix of ['_first', '_latest']) {
      try { document.cookie = namespace + suffix + '=; Max-Age=0; Path=/; Domain=slateandfold.com; Secure; SameSite=Lax'; } catch (_) { /* blocked */ }
    }
    pending('remove');
  }
  function uuid() {
    try { return crypto.randomUUID(); } catch (_) { return ''; }
  }
  const clickData = t => Object.fromEntries(clickKeys.filter(k => t?.[k]).map(k => [k,t[k]]));
  function fill() {
    if (!form) return;
    const first = allowed ? state?.first : null, latest = allowed ? state?.latest : null;
    const values = {
      inquiry_id: leadId, submission_page: safeUrl(pageUrl()),
      attribution_status: !privacyReady ? 'unavailable_privacy_api' : !allowed ? 'unavailable_consent' : cookieAvailable ? 'consented_30_day_window' : 'consented_current_page_only',
      lead_source: first?.source || 'unavailable', landing_page: first?.landing_page || '',
      referrer_host: first?.referrer_host || '', first_seen: first ? new Date(first.at).toISOString() : '',
      gclid: first?.gclid || '', first_click_parameters: first ? JSON.stringify(clickData(first)) : '',
      latest_source: latest?.source || '', latest_landing_page: latest?.landing_page || '', latest_campaign: latest?.utm_campaign || '',
      latest_touch_details: latest ? JSON.stringify(latest) : ''
    };
    utmKeys.forEach(k => { values[k] = first?.[k] || ''; });
    form.querySelectorAll('[data-quote-attribution]').forEach(field => { field.value = values[field.dataset.quoteAttribution] || ''; });
  }
  function confirmedSuccess() {
    if (successHandled || !allowed || !form?.querySelector('[data-slate-quote-success]') || form.querySelector('[data-slate-quote-error]')) return;
    const attempt = pending('get');
    if (!attempt || !/^[a-f0-9-]{36}$/.test(attempt.id) || attempt.page !== safeUrl(pageUrl())
      || !Number.isFinite(attempt.at) || Date.now() - attempt.at > 30 * 60000 || attempt.at > Date.now()) return;
    const payload = {
      inquiry_id: attempt.id, lead_source: category(attempt.latest || attempt.first),
      first_source: category(attempt.first), latest_source: category(attempt.latest),
      form_id: 'slate_custom_quote', site_market: location.hostname.startsWith('au.') ? 'au' : 'main'
    };
    if (!live) {
      successHandled = true; pending('remove');
      document.dispatchEvent(new CustomEvent('slate:quote-qa-success', { detail: { event: 'generate_lead', ...payload, test_only: true } }));
      return;
    }
    // Reuse the verified existing Google tag. Never create/configure a second tag.
    if (typeof window.gtag !== 'function') {
      if (retryCount++ < 15) setTimeout(confirmedSuccess, 1000);
      return;
    }
    // Consume pending before enqueueing: reload/back must not emit a second event.
    successHandled = true; pending('remove');
    window.gtag('event', 'generate_lead', { ...payload, send_to: script.dataset.ga4Id,
      page_location: safeUrl(pageUrl()), page_referrer: '', transport_type: 'beacon' });
  }
  function refreshPrivacy() {
    const privacy = window.Shopify?.customerPrivacy;
    privacyReady = typeof privacy?.analyticsProcessingAllowed === 'function' && typeof privacy?.marketingAllowed === 'function';
    let next = false;
    try { next = privacyReady && privacy.analyticsProcessingAllowed() && privacy.marketingAllowed(); } catch (_) { /* fail closed */ }
    allowed = !!next;
    if (!allowed) clearTracking();
    else {
      const first = readCookie('_first') || state?.first || entry;
      const last = readCookie('_latest') || state?.latest || null;
      state = { first, latest: entry.attributed ? entry : last };
      cookieAvailable = saveCookie('_first', first);
      if (state.latest) cookieAvailable = saveCookie('_latest', state.latest) && cookieAvailable;
    }
    fill(); confirmedSuccess();
  }
  function start() {
    form = document.querySelector('#contact-form');
    leadId = uuid();
    if (form) {
      if (form.querySelector('[data-slate-quote-error]')) pending('remove');
      // Native submission is untouched. A click, invalid form or AJAX start is not a lead.
      form.addEventListener('submit', () => {
        if (!form.checkValidity()) return;
        leadId = uuid(); refreshPrivacy(); fill();
        if (allowed && leadId) pending('set', { id: leadId, at: Date.now(), page: safeUrl(pageUrl()), first: state?.first?.source, latest: state?.latest?.source });
      });
    }
    document.addEventListener('visitorConsentCollected', refreshPrivacy);
    if (window.Shopify?.customerPrivacy) refreshPrivacy();
    else {
      fill();
      if (typeof window.Shopify?.loadFeatures === 'function') {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => { if (!error) refreshPrivacy(); });
      }
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
